import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { toast } from "sonner";
import {
  ChatCircleText,
  CircleNotch,
  XCircle,
  CalendarBlank,
  MapPin,
  Users,
} from "@phosphor-icons/react";

/**
 * AdminEventInquiriesCard — Phase 3 moderation feed.
 *
 * Lists every event inquiry the marketplace has received, newest-first,
 * with per-status recipient counts (so we can spot "0 of 7 replied"
 * spam-shaped inquiries fast). Admins can close abusive inquiries —
 * the host's status page reflects the close immediately.
 *
 * Mounts inside the existing Admin page CollapsibleSection so it's not
 * dashboard real-estate by default.
 */

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString([], {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function countsSummary(c) {
  if (!c || typeof c !== "object") return "—";
  const parts = [];
  if (c.reply) parts.push(`${c.reply} replied`);
  if (c.decline) parts.push(`${c.decline} declined`);
  const pending = (c.delivered || 0) + (c.viewed || 0);
  if (pending) parts.push(`${pending} pending`);
  return parts.length ? parts.join(" · ") : "no responses yet";
}

export default function AdminEventInquiriesCard() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [closingId, setClosingId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url =
        statusFilter === "all"
          ? "/admin/event-inquiries?limit=50"
          : `/admin/event-inquiries?status=${encodeURIComponent(statusFilter)}&limit=50`;
      const { data } = await api.get(url);
      setRows(data.inquiries || []);
    } catch (e) {
      toast.error(formatApiError(e) || "Could not load event inquiries");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const onClose = async (inq) => {
    const ok = window.confirm(
      `Close inquiry from ${inq.customer_email}? Owners already received the email — this hides it from the host's status page going forward.`
    );
    if (!ok) return;
    setClosingId(inq.id);
    try {
      await api.post(`/admin/event-inquiries/${inq.id}/close`);
      toast.success("Inquiry closed");
      load();
    } catch (e) {
      toast.error(formatApiError(e) || "Could not close inquiry");
    } finally {
      setClosingId(null);
    }
  };

  return (
    <div
      className="rounded-2xl border border-border bg-card p-5"
      data-testid="admin-event-inquiries-card"
    >
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h3 className="text-base font-semibold flex items-center gap-2">
            <ChatCircleText weight="duotone" className="h-5 w-5 text-primary" />
            Event inquiry moderation
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Spot-check spam, close abusive inquiries. Closing won&apos;t recall the owner emails already sent.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            className="text-xs border border-input rounded-md px-2 py-1.5 bg-background"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="admin-inquiry-status-filter"
          >
            <option value="all">All</option>
            <option value="fanned_out">Fanned out</option>
            <option value="closed">Closed</option>
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="admin-inquiry-refresh-btn"
          >
            {loading ? <CircleNotch className="h-3 w-3 animate-spin" /> : "Refresh"}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-8 text-center text-sm text-muted-foreground">
          <CircleNotch className="h-5 w-5 mx-auto animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">
          No inquiries to moderate.
        </p>
      ) : (
        <ul className="space-y-3" data-testid="admin-inquiry-list">
          {rows.map((inq) => {
            const isClosed = inq.status === "closed";
            return (
              <li
                key={inq.id}
                className={
                  "rounded-xl border p-3 sm:p-4 " +
                  (isClosed
                    ? "border-rose-200 bg-rose-50/40"
                    : "border-border bg-background")
                }
                data-testid={`admin-inquiry-row-${inq.id}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm">
                        {inq.customer_name || "Anonymous"}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        &lt;{inq.customer_email}&gt;
                      </span>
                      {isClosed && (
                        <span className="text-[10px] font-semibold text-rose-700 bg-rose-100 px-1.5 py-0.5 rounded uppercase tracking-wide">
                          Closed
                        </span>
                      )}
                    </div>
                    <div className="mt-1.5 flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                      <span className="flex items-center gap-1">
                        <CalendarBlank weight="duotone" className="h-3.5 w-3.5" />
                        {inq.event_type} · {inq.event_date_start}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin weight="duotone" className="h-3.5 w-3.5" />
                        {inq.event_zip}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users weight="duotone" className="h-3.5 w-3.5" />
                        {inq.party_size}
                      </span>
                    </div>
                    {inq.message && (
                      <p className="mt-2 text-xs text-foreground/80 line-clamp-2 whitespace-pre-wrap">
                        {inq.message}
                      </p>
                    )}
                    <div className="mt-2 text-[11px] text-muted-foreground">
                      Sent to {inq.fanned_out_to_count} {inq.fanned_out_to_count === 1 ? "truck" : "trucks"} · {countsSummary(inq.recipient_counts)} · {fmt(inq.created_at)}
                    </div>
                  </div>
                  <div className="flex-none">
                    {!isClosed && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onClose(inq)}
                        disabled={closingId === inq.id}
                        data-testid={`admin-inquiry-close-btn-${inq.id}`}
                      >
                        {closingId === inq.id ? (
                          <CircleNotch className="h-3 w-3 animate-spin" />
                        ) : (
                          <>
                            <XCircle weight="bold" className="h-3.5 w-3.5 mr-1" />
                            Close
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
